import React, { useState, useReducer, useContext, useEffect } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { set } from 'lodash'

import Fullscreen from 'react-full-screen'

import { MainContent } from '../../../../layouts/components/Structure'
import IpsaSidebar from '../../../../layouts/IpsaSidebar'
import IpsaSidebarContext from '../../../../context/SidebarContext'

import IncomeAssetsStep from './Graphs/IncomeAssets'
import AssetComparisonSeparateStep from './Graphs/AssetComparisonSeparate'
import AssetComparisonStep from './Graphs/AssetComparison'
import CurrentCoverageAnalysisStep from './Graphs/CurrentCoverageAnalysis'
import IncomeAssetWithEventStep from './Graphs/IncomeAssetWithEvent'
import AssetValueCostComparisonStep from './Graphs/AssetValueCostComparison'
import PlanComparisonStep from './Graphs/PlanComparison'
import PresentationSidebar from './components/PresentationSidebar'
import PresentationNavigation from './components/PresentationNavigation'
import PresentationContext from './PresentationContext'
import { GetClientName, GetClient } from '../../../../store/ClientsApiClient'
import { GetPresentationHeaders } from '../../../../store/ChartApiClient'

import LoadingWithText from '../../../../components/LoadingWithText/LoadingWithText'

const handleRejection = (p) => {
    return p.catch((error) => ({
        error,
    }))
}

const waitForAll = (...ps) => {
    return Promise.all(ps.map(handleRejection))
}

const Presentation = ({ url, id, graph }) => {
    const { setSidebar, fullScreen, setFullScreen } = useContext(IpsaSidebarContext)
    const [loading, setLoading] = useState(true)

    const CurrentCoverageLoaded = () => {
        setPresentationContext({ name: 'currentCoverage.loaded', value: true })
    }

    const SetSliderEnabled = (isEnabled) => {
        setPresentationContext({ name: 'slider.enabled', value: isEnabled })
    }

    const UpdateDomain = (domain) => {
        setPresentationContext({ name: 'slider.domain', value: domain })
    }

    const UpdateValues = (values) => {
        setPresentationContext({ name: 'slider.values', value: values })
    }

    const UpdateEventSettings = (values) => {
        setPresentationContext({ name: 'slider.eventSettings', value: values })
    }

    const SetMarginLeft = (value) => {
        setPresentationContext({ name: 'slider.marginLeft', value: value })
    }

    const SetSliderWidth = (value) => {
        setPresentationContext({ name: 'slider.sliderWidth', value: value })
    }

    const SetClientName = (value) => {
        setPresentationContext({ name: 'clientName', value: value })
    }

    const SetCoverageLevels = (value) => {
        setPresentationContext({ name: 'coverageLevels', value: value })
    }

    const SetPresentationHeaders = (value) => {
        setPresentationContext({ name: 'presentationHeaders', value: value })
    }

    const SetClientNameFromApi = async (id) => {
        const { data } = await GetClientName(id)
        SetClientName(data)
    }

    const SetCoverageLevelsFromApi = async (id) => {
        const { data } = await GetClient(id)
        if (data.protections) {
            const coverageLevels = []
            const { protections } = data

            const { comprehensive, moderate, basic } = protections
            if (comprehensive && comprehensive.benefitAmount > 0) {
                coverageLevels.push({
                    key: 'Comprehensive',
                    value: comprehensive.name ?? 'Comprehensive',
                })
            }

            if (moderate && moderate.benefitAmount > 0) {
                coverageLevels.push({
                    key: 'Moderate',
                    value: moderate.name ?? 'Moderate',
                })
            }
            if (basic && basic.benefitAmount > 0) {
                coverageLevels.push({
                    key: 'Basic',
                    value: basic.name ?? 'Basic',
                })
            }

            SetCoverageLevels(coverageLevels)
        }
    }

    const SetPresentationHeadersFromApi = async (id) => {
        const { data } = await GetPresentationHeaders(id)
        SetPresentationHeaders(data)
    }

    useEffect(() => {
        waitForAll(
            SetClientNameFromApi(id),
            SetCoverageLevelsFromApi(id),
            SetPresentationHeadersFromApi(id)
        ).finally(() => {
            setLoading(false)
            setSidebar(false)
        })
    }, [])

    const InitialState = {
        clientName: '',
        currentCoverage: {
            loaded: false,
            setLoaded: CurrentCoverageLoaded,
        },
        presentationHeaders: {
            showIncome: false,
            showAssets: false,
            showCurrentCoverage: false,
            showIncomeLoss: false,
            showPlanComparison: false,
            showCoverageScenarios: false,
            showBenefitCostComparison: false,
        },
        slider: {
            marginLeft: 0,
            sliderWidth: 0,
            enabled: false,
            domain: [0, 100],
            values: [0, 1],
            eventSettings: {
                start: null,
                end: null,
            },
            setMarginLeft: SetMarginLeft,
            setSliderWidth: SetSliderWidth,
            setSliderEnabled: SetSliderEnabled,
            updateDomain: UpdateDomain,
            updateValues: UpdateValues,
            updateEventSettings: UpdateEventSettings,
        },

        coverageLevels: [
            { key: 'Comprehensive', value: 'Comprehensive' },
            { key: 'Moderate', value: 'Moderate' },
            { key: 'Basic', value: 'Basic' },
        ],
    }

    const [presentationContext, setPresentationContext] = useReducer((state, action) => {
        const { name, value } = action
        const updatePath = name.split('.')
        return set({ ...state }, updatePath, value)
    }, InitialState)

    const ToggleFullScreen = () => {
        setFullScreen(!fullScreen)
    }

    return (
        <PresentationContext.Provider value={presentationContext}>
            <IpsaSidebar>
                <PresentationSidebar graph={graph} />
            </IpsaSidebar>
            <MainContent>
                <Fullscreen enabled={fullScreen} onChange={(fs) => setFullScreen(fs)}>
                    <PresentationNavigation
                        url={url}
                        graph={graph}
                        toggleFullScreen={ToggleFullScreen}
                    />

                    {loading ? (
                        <LoadingWithText text="Loading Presentation" />
                    ) : (
                        <Switch>
                            <Redirect
                                from="/clients/:id/presentation"
                                to="/clients/:id/presentation/income"
                                exact
                            />
                            <Route
                                path="/clients/:id/presentation/income"
                                render={() => <IncomeAssetsStep id={id} />}
                            />
                            <Route
                                path="/clients/:id/presentation/assets"
                                render={() => <AssetComparisonStep id={id} />}
                            />
                            <Route
                                path="/clients/:id/presentation/with-event-loss"
                                render={() => (
                                    <IncomeAssetWithEventStep
                                        key={id + 'loss'}
                                        id={id}
                                        incomeloss={true}
                                    />
                                )}
                            />
                            <Route
                                path="/clients/:id/presentation/coverage"
                                render={() => <CurrentCoverageAnalysisStep id={id} />}
                            />
                            <Route
                                path="/clients/:id/presentation/with-event"
                                render={() => (
                                    <IncomeAssetWithEventStep
                                        key={id + 'coverage'}
                                        id={id}
                                        incomeloss={false}
                                    />
                                )}
                            />
                            <Route
                                path="/clients/:id/presentation/plans"
                                render={() => <PlanComparisonStep id={id} />}
                            />
                            <Route
                                path="/clients/:id/presentation/asset-value"
                                render={() => <AssetValueCostComparisonStep id={id} />}
                            />
                        </Switch>
                    )}
                </Fullscreen>
            </MainContent>
        </PresentationContext.Provider>
    )
}

export default Presentation
